/**
 * scheduleLinkBannerText.ts — pure copy builders for ScheduleLinkBanner.
 *
 * Extracted from the component (banner redesign, v1.0.22) so the wording is
 * unit-testable without React. All strings are sentence case per the redesign:
 * the old ALL-CAPS-ish pill copy ("1 task(s) whose run mode changed") became
 * natural phrasing ("1 task's run mode changed").
 */

import type { SourceScheduleImpact } from '../../services/scheduleScenario';

/** "s" when n !== 1 — tiny plural helper for the copy below. */
const s = (n: number): string => (n === 1 ? '' : 's');

/**
 * Title line: "This quote has a schedule — SP-091 · 9 of 10 tasks planned".
 * Falls back to the G2-4 reduced variant ("scheduled directly on the live
 * board") when schedule rows exist without a Scenario row.
 */
export function formatBannerTitle(impact: SourceScheduleImpact): string {
  const sourceNoun = impact.sourceType === 'Quote' ? 'quote' : 'work order';
  const primary = impact.scenarios[0];
  if (primary) {
    const id = primary.code || primary.name || 'a plan';
    if (
      primary.plannedness === 'full' ||
      (primary.taskCount > 0 && primary.plannedTaskCount >= primary.taskCount)
    ) {
      return `This ${sourceNoun} has a schedule — ${id} · all ${primary.taskCount} task${s(primary.taskCount)} planned`;
    }
    if (primary.plannedness === 'empty' || primary.plannedTaskCount === 0) {
      return `This ${sourceNoun} has a schedule — ${id} · no tasks planned yet`;
    }
    return `This ${sourceNoun} has a schedule — ${id} · ${primary.plannedTaskCount} of ${primary.taskCount} tasks planned`;
  }
  if (impact.hasScenario) return `This ${sourceNoun} has an attached schedule`;
  const taskCount = impact.totals?.taskCount ?? 0;
  return `This ${sourceNoun} is scheduled directly on the live board — ${taskCount} task${s(taskCount)}, no plan attached`;
}

/**
 * Quiet stats line under the title: "2 plans attached · 18 tentative
 * bookings · 3 committed". Empty string when there is nothing to say.
 */
export function formatBannerStats(impact: SourceScheduleImpact): string {
  const parts: string[] = [];
  if (impact.hasScenario && impact.scenarios.length > 0) {
    parts.push(`${impact.scenarios.length} plan${s(impact.scenarios.length)} attached`);
  }
  const totals = impact.totals;
  if (totals) {
    parts.push(`${totals.tentativeBookings} tentative booking${s(totals.tentativeBookings)}`);
    parts.push(`${totals.committedBookings} committed`);
    // Item 4.2 (index tests, Phase 3): only mentioned when the source actually
    // has pooled index allocations — most sources have none, so the common
    // case reads exactly as before.
    if (totals.indexAllocationCount) {
      parts.push(`${totals.indexAllocationCount} index test type${s(totals.indexAllocationCount)}`);
    }
  }
  return parts.join(' · ');
}

/**
 * Drift summary for the "Out of sync" chip. Covers every stale source the
 * backend reports: added/removed/changed line items (FEAT-2026-0012, P-8b,
 * W2-2), machine-rate drift (G2-11), and run-mode drift (RM-16 — an admin
 * flipped a test type between attended and continuous 24/7; Sync adopts it).
 */
export function formatStaleDetail(stale: SourceScheduleImpact['stale'] | undefined): string {
  const parts: string[] = [];
  if (stale?.addedLineItems) parts.push(`${stale.addedLineItems} new line item${s(stale.addedLineItems)}`);
  if (stale?.removedTasks) parts.push(`${stale.removedTasks} removed task${s(stale.removedTasks)}`);
  if (stale?.changedLineItems) parts.push(`${stale.changedLineItems} changed line item${s(stale.changedLineItems)}`);
  if (stale?.rateDriftTasks) {
    parts.push(`${stale.rateDriftTasks} task${s(stale.rateDriftTasks)} sized with an outdated machine rate`);
  }
  if (stale?.runModeDriftTasks) {
    parts.push(
      stale.runModeDriftTasks === 1
        ? "1 task's run mode changed"
        : `${stale.runModeDriftTasks} tasks' run modes changed`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : 'line items changed';
}
